import * as path from "path";
import { Readable } from "stream";
import { Agent } from "https";

import fetch from "node-fetch";
import pLimit, { Limit } from "p-limit";
import { pathExists } from "fs-extra";

import type { BuildTask } from "../../../../../../Generator/src/generators/generate";
import { generateForm } from "../../../../BuildTasks/Form/GenerateForm";
import { generatorFactory, getGeneratorSourceKey } from "../../../../BuildTasks/Generator/AcquireFormGenerator";
import {
    resolveEngineVersion,
    resolveGeneratorVersion,
    resolveLocalEngineSource,
    resolveLocalGeneratorSource,
} from "../../../../BuildTasks/PublishedVersionUtils";
import { resolveFormInfo } from "../../../../BuildTasks/ResolveFormInfo";
import { delay } from "../../../../../../Common/AsyncUtils";
import { ReasonOrError } from "../../../../Commons/ErrorUntyped";
import { ChainableValueProducer } from "../../../../Commons/TasksCore/ChainableValueProducer";
import { Observe } from "../../../../Commons/TasksCore/Observe";
import { extractGFVPathFromCandyFarmPath } from "../../../../../../Common/ExtractGFVPath/ExtractGFVPath";
import { WellKnownDirectories } from "../../../../../../Common/WellKnownDirectories";
import { NotFoundWebError } from "../../../../Commons/HttpServer/AttributeRouting/NotFoundWebError";
import { ensureNotNullOrUndefined } from "../../../../../../Common/TypingUtils";

import { getReportGeneratorErrorToPageBodyScript } from "./ReportGeneratorErrorToPageBody";
import { runFormCompilationViaWebpackDevServer } from "./RunFormCompilationViaWebpackDevServer";
import { WebpackHybridFileSystem } from "./WebpackInMemoryUtils/WebpackHybridFileSystem";

export class BuildFormDevServer {
    private readonly port: number;
    private readonly allFarmConfig: string[];
    private readonly gfv: string;
    private readonly candyModulesDirectory: string;
    private farmDirectory: string;
    private formPath: string;
    private readonly startParallelLimit: Limit;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private generateChain?: ChainableValueProducer<any>;
    private webpackDevServerStarted = false;
    private readonly useGeneratorFromLocalSources: boolean;
    private readonly engineDirectory: string;
    private readonly nightly: boolean;
    private readonly buildDebugOnly: boolean;
    private webpackHybridFileSystem?: WebpackHybridFileSystem;
    private readonly httpsAgent = new Agent({ rejectUnauthorized: false, keepAlive: true });

    public constructor(
        port: number,
        allFarmConfig: string[],
        gfv: string,
        candyModulesDirectory: string,
        useGeneratorFromLocalSources: boolean,
        engineDirectory: string,
        buildDebugOnly: boolean,
        nightly: boolean
    ) {
        this.port = port;
        this.allFarmConfig = allFarmConfig;
        this.gfv = gfv;
        this.candyModulesDirectory = candyModulesDirectory;
        this.startParallelLimit = pLimit(1);
        this.useGeneratorFromLocalSources = useGeneratorFromLocalSources;
        this.engineDirectory = engineDirectory;
        this.buildDebugOnly = buildDebugOnly;
        this.nightly = nightly;
        this.farmDirectory = "";
        this.formPath = "";
    }

    private async findFormPath(gfv: string): Promise<{ formPath: string; farmDirectory: string }> {
        for (const farmConfigPath of this.allFarmConfig) {
            const formPath = extractGFVPathFromCandyFarmPath(farmConfigPath, gfv);
            if (await pathExists(formPath)) {
                return {
                    formPath: formPath,
                    farmDirectory: farmConfigPath,
                };
            }
        }

        return this.findFormPathLegacy(this.allFarmConfig, gfv);
    }

    // Fallback для legacy-каталогов (без /Candy подпапки)
    // узнать остались ли такие, если нет -> удалить
    private async findFormPathLegacy(
        allFarmConfig: string[],
        gfv: string
    ): Promise<{ formPath: string; farmDirectory: string }> {
        let farmDirectory: string | undefined;
        let formDirPath = "";

        const allPossibleFilePath = allFarmConfig.map(i => path.join(i, "forms", gfv));
        for (const pth of allPossibleFilePath) {
            if (await pathExists(pth)) {
                formDirPath = pth;
                farmDirectory = path.join(pth, "..", "..");
                break;
            }
        }

        if (formDirPath === "") {
            throw Error("formDirPath not found");
        }
        if (farmDirectory == undefined || !(await pathExists(farmDirectory))) {
            throw Error(`FarmDirectory = ${farmDirectory} not exist`);
        }
        return {
            formPath: formDirPath,
            farmDirectory: farmDirectory,
        };
    }

    private async writeErrorInEntryAndShowInBrowser(e: unknown) {
        const formEntryPointPath = path.join(this.formPath, "form", "web.js");
        await this.webpackHybridFileSystem?.writeBatch({
            [formEntryPointPath]: getReportGeneratorErrorToPageBodyScript(this.formatError(e)),
        });
    }

    public start(): Promise<void> {
        return this.startParallelLimit(async () => {
            if (this.generateChain != undefined) {
                return;
            }

            const { formPath, farmDirectory } = await this.findFormPath(this.gfv);
            this.farmDirectory = farmDirectory;
            this.formPath = formPath;
            this.webpackHybridFileSystem = new WebpackHybridFileSystem(path.join(this.formPath, "form"));

            this.generateChain = Observe.file(path.join(this.formPath, WellKnownDirectories.FormJsonFileName))
                .transform(formJsonFile =>
                    resolveFormInfo(
                        formJsonFile,
                        this.formPath,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        this.nightly
                    )
                )
                .transform(async x => {
                    if (this.useGeneratorFromLocalSources) {
                        return {
                            ...x,
                            generatorSource: resolveLocalGeneratorSource(this.engineDirectory),
                            engineSource: resolveLocalEngineSource(this.engineDirectory),
                        };
                    } else {
                        if (x.generatorVersion == undefined) {
                            throw new Error("generatorVersion is no defined");
                        }
                        if (x.engineVersion == undefined) {
                            throw new Error("engineVersion is no defined");
                        }
                        return {
                            ...x,
                            generatorSource: await resolveGeneratorVersion(x.generatorVersion),
                            engineSource: await resolveEngineVersion(x.engineVersion),
                        };
                    }
                })
                .attach(
                    async fi => generatorFactory(fi.generatorSource, this.candyModulesDirectory, false, undefined),
                    async (fi, y) => ({ ...fi, generator: y }),
                    async fi => getGeneratorSourceKey(fi.generatorSource)
                )
                .attach(async fi =>
                    Observe.files(
                        fi.formSourcesPath,
                        "../../controls/**/generator/*",
                        `./${WellKnownDirectories.FormJsonFileName}`,
                        "schemas/**/*",
                        "sugar/**/*",
                        "controls/**/*",
                        "autocalc/**/*",
                        "helpers/**/*",
                        "normalizers/**/*",
                        "validations/**/*",
                        "localization/**/*"
                    )
                )
                .do(async fi => {
                    try {
                        const task: BuildTask = {
                            gfv: fi.formName,
                            formPath: fi.formSourcesPath,
                            farmDirectory: farmDirectory,
                            resourcesHash: "local-hash",
                        };
                        await generateForm(task, fi.generator, undefined, this.webpackHybridFileSystem);
                    } catch (e) {
                        await this.writeErrorInEntryAndShowInBrowser(e);
                    }
                });
            try {
                await this.generateChain.observe();
            } catch (e) {
                await this.writeErrorInEntryAndShowInBrowser(e);
            }

            await runFormCompilationViaWebpackDevServer(
                this.gfv,
                this.formPath,
                path.join(this.farmDirectory, "res"),
                "local-hash",
                this.buildDebugOnly,
                this.webpackHybridFileSystem,
                this.port
            );
            this.webpackDevServerStarted = true;
        });
    }

    public async getGeneratedFileAsStream(filename: string): Promise<NodeJS.ReadableStream> {
        while (!this.webpackDevServerStarted) {
            await delay(500);
        }

        const { formPath } = await this.findFormPath(this.gfv);
        const filePath = path.join(formPath, WellKnownDirectories.GeneratedFormSubdirectory, filename);
        try {
            ensureNotNullOrUndefined(this.webpackHybridFileSystem);
            this.webpackHybridFileSystem.statSync(filePath);
        } catch {
            throw new NotFoundWebError(`getGeneratedFileAsStream ${filename} not found`);
        }

        return new Promise((resolve, reject) => {
            this.webpackHybridFileSystem?.readFile(filePath, "utf-8", (error, data) => {
                if (error) {
                    reject(error);
                } else {
                    resolve(Readable.from([data]));
                }
            });
        });
    }

    public async getFileAsStream(filename: string): Promise<NodeJS.ReadableStream> {
        while (!this.webpackDevServerStarted) {
            await delay(500);
        }
        const response = await fetch(`https://localhost:${this.port}/${filename}`, {
            method: "GET",
            agent: this.httpsAgent,
        });
        if (response.status != 200) {
            const textResponse = await response.text();
            throw new Error(textResponse);
        }
        return response.body;
    }

    private formatError(e: ReasonOrError): string {
        if (e instanceof Error) {
            return `${e}\n${e.stack}`;
        }
        return `${e}`;
    }
}
